import PrimaryButton from '@/components/Buttons/PrimaryButton';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import AppLayout from '@/layouts/AppLayout';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import OnboardingTabBar from '../Home/Partials/Onboarding/OnboardingTabBar';
import OnboardingTabPanel from '../Home/Partials/Onboarding/OnboardingTabPanel';
import { OnboardingData } from '../Home/Partials/Onboarding/OnboardingSection';

interface Props {
    onboarding?: OnboardingData | null;
}

const HelpContent: React.FC<Props> = ({ onboarding }) => {
    const { t } = useTranslation();
    const { route } = useRoutes();
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const activeTab = onboarding?.tabs[activeTabIndex];
    const hasOnboardingVideos = Boolean(onboarding && onboarding.tabs.length > 0);

    return (
        <AppLayout>
            <PageTitle title="Ayuda" />
            <div className="mx-auto max-w-7xl space-y-8">
                {/* Page Header */}
                <div className="space-y-4">
                    <h1 className="text-4xl font-bold text-gray-900 dark:text-white sm:text-5xl">
                        {t('help.title')}
                    </h1>
                    <p className="text-lg text-gray-700 dark:text-neutral-300 sm:text-xl">
                        {t('help.description')}
                    </p>
                </div>

                {/* Main Content Grid */}
                <div className="grid gap-8 lg:grid-cols-3">
                    {/* Video Section - Takes 2 columns */}
                    <div className="space-y-4 lg:col-span-2">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                            {t('help.welcomeVideo')}
                        </h2>
                        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-sm">
                            {hasOnboardingVideos ? (
                                <div className="space-y-4 p-6">
                                    <OnboardingTabBar
                                        tabs={onboarding.tabs}
                                        activeIndex={activeTabIndex}
                                        onTabChange={setActiveTabIndex}
                                    />
                                    {activeTab ? <OnboardingTabPanel tab={activeTab} /> : null}
                                </div>
                            ) : (
                                <div
                                    className="relative"
                                    style={{ paddingTop: '56.25%' }}
                                >
                                    <iframe
                                        src="https://iframe.mediadelivery.net/embed/135359/5ad2da2a-c65c-4db8-a5c0-674d169a2d45?autoplay=false&loop=false&muted=false&preload=true&responsive=true"
                                        loading="lazy"
                                        style={{
                                            border: 0,
                                            position: 'absolute',
                                            top: 0,
                                            height: '100%',
                                            width: '100%',
                                        }}
                                        allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                                        allowFullScreen={true}
                                        title={t('help.welcomeVideo')}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Support Section - Takes 1 column */}
                    <div className="space-y-6">
                        <div className="rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-6 shadow-sm">
                            <h3 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                                {t('help.problemTitle')}
                            </h3>
                            <PrimaryButton
                                as="link"
                                href="https://conquerx.freshdesk.com/support/tickets/new"
                                target="_blank"
                                size="full-width"
                            >
                                {t('help.openTicket')}
                            </PrimaryButton>
                        </div>
                        <div className="rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-6 shadow-sm">
                            <h3 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                                {t('help.certificateRequestTitle')}
                            </h3>
                            <PrimaryButton
                                as="link"
                                href={route(
                                    'enrollment_certificate'
                                ).toString()}
                                size="full-width"
                            >
                                {t('help.certificateRequestAction')}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

const Help: React.FC<Props> = ({ onboarding }) => (
    <ThemeWrapper>
        <HelpContent onboarding={onboarding} />
    </ThemeWrapper>
);

export default Help;
